import { useLayoutEffect, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useAppState } from '@/core/state/react'
import { uiActions } from '@/core/state/ui'
import { StatCards } from './StatCards'
import { Toolbar } from './Toolbar'
import './statbar.css'

/**
 * The summary stat bar's shell (Phase 1G), replacing
 * `features/summary-statistics/components/StatsComparisonBar.vue`.
 *
 * The frame is React: the fixed grey band under the app bar, the action row
 * holding the toolbar, and the grey extension with its blue rule. Since Phase
 * 1I the cards are React too — the `statCards` island is gone.
 *
 * It publishes the edge the rest of the page lays out against. MapView used to
 * measure the Vue bar's `$el` for this; it now reads `uiStore.chromeBottom`.
 * The edge is this element's own box. The extension and the rule are drawn
 * below it, absolutely positioned, and are deliberately not part of it — that
 * is what MapView measured, and the map starts under the band, not the rule.
 *
 * It is SHOWN once MapView has initialised its scenarios, and hidden until
 * then — `v-show`, exactly how the Vue bar was gated, not `v-if`. Mounting it
 * only when the scenarios are ready measured badly when the cards were an
 * island (~300ms of Vue app creation at the worst moment: the band appeared
 * card-less at 782ms and only filled at 1077ms). The island is gone, but the
 * reason to keep this mounted is not: hidden, it still runs its queries, so the
 * bar appears complete rather than filling in after the map.
 *
 * Hidden, it publishes no edge and clears the one it published, so nothing
 * lays out against a band that is not on screen.
 */
export function StatBar() {
  const ready = useAppState((s) => s.scenariosReady)
  return <Shell hidden={!ready} />
}

function Shell({ hidden }: { hidden: boolean }) {
  const shell = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = shell.current
    if (!el) return
    // Hidden: there is no edge. Publishing nothing would leave the page laid
    // out against the band it can no longer see (MapView unmounting behind an
    // error boundary hides the bar, and MapView's watcher is `immediate`).
    if (hidden) {
      uiActions.clearChromeBottom()
      return
    }
    const publish = () => {
      const r = el.getBoundingClientRect()
      if (r.height > 0) uiActions.setChromeBottom(r.bottom)
    }
    publish()
    // Its height is content-driven: the card row arrives with the island's
    // chunk and changes with the cards shown. The window listener covers what
    // moves the box without resizing it.
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    window.addEventListener('resize', publish)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', publish)
    }
  }, [hidden])

  return (
    <div ref={shell} hidden={hidden} className="scvc-stat-bar stats-comparison-bar" data-testid="stat-bar-shell">
      <div className="scvc-stat-bar__content">
        {/* The cards' own boundary, deliberately not around the bar. The
            toolbar holds MENU — the way to everything — so it must not fail
            with the cards: four queries and a pile of arithmetic feed them, and
            an error thrown there would otherwise take the whole app to the
            frame's error screen. Here, a failed card row costs the card row.

            The Suspense that wrapped this is gone with the island: the cards
            are part of this chunk now, and nothing here suspends. */}
        <ErrorBoundary
          fallback={<div className="scvc-stat-bar__cards-failed" role="alert" data-testid="stat-cards-failed" />}
          onError={(error) => console.error('[stat bar] the stat cards failed', error)}
        >
          <StatCards />
        </ErrorBoundary>
        <div className="scvc-stat-bar__actions">
          <Toolbar />
        </div>
      </div>
      <div className="scvc-stat-bar__extension" aria-hidden="true">
        <div className="scvc-stat-bar__rule" />
      </div>
    </div>
  )
}
